<template>
    <div class="product-posting-list">
      <p v-if="isLoading" style="color: blue">Đang thao tác với dữ liệu...</p>
      
      <!-- Mobile Card View (shown on mobile screens via CSS) -->
      <div class="mobile-view">
        <div class="mobile-filters mb-3">
          <b-button @click="openFilterDrawer" variant="primary" size="sm" class="w-100">
            <b-icon icon="funnel"></b-icon> Bộ lọc
            <b-badge v-if="activeFiltersCount > 0" variant="light" class="ml-2">{{activeFiltersCount}}</b-badge>
          </b-button>
        </div>
        
        <div class="mobile-summary mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Số lượng: {{totalCount}}</span>
            
          </div>
        </div>

        <!-- Mobile Cards -->
        <div v-if="isLoading" class="mobile-loading">
          <div class="text-center py-4">
            <b-spinner variant="primary"></b-spinner>
            <p class="mt-2">Đang tải dữ liệu...</p>
          </div>
        </div>
        
        <div v-else-if="mobileData.length === 0" class="mobile-empty">
          <div class="text-center py-4">
            <b-icon icon="inbox" font-scale="3" class="text-muted"></b-icon>
            <p class="mt-2 text-muted">Không có dữ liệu</p>
          </div>
        </div>
        
        <div v-else class="mobile-cards">
          <div v-for="item in mobileData" :key="item.id" class="mobile-card mb-3" :class="item._rowVariant">
            <div class="card-header d-flex justify-content-between align-items-start">
              <div class="card-title">
                <strong>{{item.code}}</strong>
                <small class="text-muted d-block">{{item.fullname}}</small>
              </div>
              <div class="card-actions">
                <b-icon :icon="item.isfavored ? 'star-fill' : 'star'" 
                       @click="updateStar({id: item.id, isfavored: item.isfavored})" 
                       font-scale="1.2" 
                       class="clickable text-warning"></b-icon>
                <b-dropdown size="sm" variant="link" class="ml-2">
                  <template #button-content>
                    <b-icon icon="three-dots-vertical"></b-icon>
                  </template>
                  <b-dropdown-item @click="rowClicked({id: item.id})">Xem chi tiết</b-dropdown-item>
                  <b-dropdown-item @click="rowEditClicked({id: item.id})">Chỉnh sửa</b-dropdown-item>
                  <b-dropdown-item @click="menuClicked({menu_code: menu_item.code, id: item.id})" 
                                 v-for="menu_item in menu" :key="menu_item.code">
                    {{menu_item.text}}
                  </b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item @click="rowDeleteClicked({id: item.id})" class="text-danger">Xóa</b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            
            <div class="card-body">
              <div class="info-row">
                <span class="label">Địa chỉ:</span>
                <span class="value">{{item.address}}</span>
              </div>
              <div class="info-row">
                <span class="label">Đường:</span>
                <span class="value">{{item.street}}</span>
              </div>
              <div class="info-row">
                <span class="label">Trạng thái:</span>
                <span class="value" :class="getStatusClass(item.posting_status)">{{item.posting_status}}</span>
              </div>
              <div class="info-row" v-if="item.last_posting_date">
                <span class="label">Lần đăng gần nhất:</span>
                <span class="value">{{formatDate(item.last_posting_date)}}</span>
              </div>
              <div class="info-row" v-if="item.is_public !== undefined">
                <span class="label">Hiển thị cho CTV:</span>
                <span class="value">
                  {{ Number(item.is_public) === 1 ? 'Có' : 'Không' }}
                </span>
              </div>
            </div>
            
            <div class="card-footer">
              <div class="d-flex justify-content-between align-items-center">
                <b-button size="sm" variant="outline-primary" @click="rowClicked({id: item.id})">
                  <b-icon icon="eye"></b-icon> Xem
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="rowEditClicked({id: item.id})">
                  <b-icon icon="pencil"></b-icon> Sửa
                </b-button>
                <b-button size="sm" variant="outline-info" @click="copyToClipboard(item.code)">
                  <b-icon icon="clipboard"></b-icon> Copy MSP
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Pagination -->
        <div class="mobile-pagination mt-4">
          <b-pagination
            v-model="mobilePage"
            :total-rows="mobilePagination.total"
            :per-page="mobilePagination.limit"
            :limit="5"
            size="sm"
            aria-controls="my-table"
            class="justify-content-center"
          ></b-pagination>
        </div>
      </div>

      <!-- Desktop Table View (shown on desktop screens via CSS) -->
      <div class="desktop-view">
        <div class="desktop-filters mb-3">
          <b-button @click="openFilterDrawer" variant="outline-primary" size="sm">
            <b-icon icon="funnel"></b-icon> Bộ lọc
            <b-badge v-if="activeFiltersCount > 0" variant="primary" class="ml-2">{{activeFiltersCount}}</b-badge>
          </b-button>
        </div>
        
        <table-view
            :columns="columns"
            :display_columns="display_columns"
            :filters="filters"
            :table_name="table_name"
            :sorts="sorts"
            :rowPreprocessFunc="rowPreprocessFunc"
            :menu="menu"
            module_name="product_posting"
            @row-clicked="rowClicked"
            @row-edit-clicked="rowEditClicked"
            @row-delete-clicked="rowDeleteClicked"
            @update-group-clicked="updateGroupClicked"
            @delete-group-clicked="deleteGroupClicked"
            @menu-clicked="menuClicked"
            @star-clicked="updateStar"
            :groupEditable="false"
            :itemEditable="false"
            ref="tableview"
            :hasStars="true"
            :hideFilterButton="true"
        >
        </table-view>
      </div>

      <!-- Filter Drawer -->
      <filter-drawer
        :show="showFilterDrawer"
        :filters="filters"
        :columns="columns"
        :fallback-options="fallbackFilterOptions"
        @close="closeFilterDrawer"
        @clear-filters="clearAllFilters"
        @apply-filters="applyFilters"
      />
    </div>
</template>

<script>

import ProductPostingSummaryTableMeta from '@/models/ProductPostingSummaryTableMeta'
import GenericTableView from '@/views/base/GenericTableView'
import FilterDrawer from '@/views/base/FilterDrawer'

import ProductPostingService from './ProductPostingService'
import Firebase from '@/services/firebase'
import moment from 'moment'

export default {
  name: 'ProductPostingList',
  components: {
      'table-view': GenericTableView,
      'filter-drawer': FilterDrawer
  },
  data () {
    return {
        display_columns: ProductPostingSummaryTableMeta.list_views.columns.default_data,
        columns: ProductPostingSummaryTableMeta.columns.default_data,
        filters: ProductPostingSummaryTableMeta.list_views.filters,
        table_name: ProductPostingSummaryTableMeta.table_name,
        sorts: ProductPostingSummaryTableMeta.list_views.sorts,
        menu: [
          {
            code: 'view_detail',
            text: 'Xem tin đăng'
          },
          {
            code: 'status_finished',
            text: 'Đã hoàn tất'
          },
          {
            code: 'status_posting',
            text: 'Đang đăng tin'
          }
        ],
        isLoading: false,
        mobileData: [],
        mobilePagination: {
          total: 0,
          limit: 20
        },
        mobilePage: 1,
        showFilterDrawer: false
    }
  },
  
  computed: {
    totalCount() {
      return this.mobilePagination.total;
    },

    activeFiltersCount() {
      let count = 0;
      this.filters.forEach(filter => {
        if (filter.value) {
          if (Array.isArray(filter.value) && filter.value.length > 0) {
            count++;
          } else if (!Array.isArray(filter.value)) {
            count++;
          }
        }
        if (filter.from || filter.to) {
          count++;
        }
      });
      return count;
    },

    fallbackFilterOptions() {
      return {
        posting_status: [
          { text: 'Đang đăng tin', value: 'Đang đăng tin' },
          { text: 'Đã hoàn tất', value: 'Đã hoàn tất' }
        ]
      };
    }
  },

  mounted() {
    // Initialize filters
    this.initializeFilters();
    // Load mobile data on mount
    this.loadMobileData();
  },


  
  methods: {
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },

    getStatusClass(status) {
      if (status === 'Đã hoàn tất') return 'text-success';
      if (status === 'Đang đăng tin') return 'text-primary';
      return '';
    },

    getDaysClass(days) {
      if (days > 7) return 'text-danger';
      if (days > 3) return 'text-warning';
      return 'text-success';
    },

    copyToClipboard(value) {
      const tempTextArea = document.createElement("textarea");
      tempTextArea.value = value;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand("copy");
      document.body.removeChild(tempTextArea);
      
      // Show feedback
      this.$bvToast.toast(`Đã copy vào clipboard: ${value}`, {
        title: 'Thành công',
        variant: 'success',
        solid: true,
        autoHideDelay: 2000
      });
    },

    async loadMobileData() {
      this.isLoading = true;
      try {
        const service = new ProductPostingService();
        const response = await service.getProductPostings({
          page: this.mobilePage,
          limit: this.mobilePagination.limit,
          filters: this.filters
        });
        this.mobileData = (response.data || []).map(item => {
          // Apply the same preprocessing as the table view
          return this.rowPreprocessFunc ? this.rowPreprocessFunc(item) : item;
        });
        this.mobilePagination.total = response.total || 0;
      } catch (error) {
        console.error('Error loading mobile data:', error);
        // Fallback: use empty data
        this.mobileData = [];
        this.mobilePagination.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    rowClicked (event) {
        console.log('row click');

        let routeData = this.$router.resolve({path: `/v2/products/${event.id}/posting`});
        window.open(routeData.href, '_blank');
        // this.$router.push({ path: `/v2/products/${event.id}` }) 
    },

    rowEditClicked (event) {
        let routeData = this.$router.resolve({path: `/v2/products/${event.id}`});
        window.open(routeData.href, '_blank');
        // this.$router.push({ path: `/v2/products/${event.id}` }) 
    },

    async rowDeleteClicked (obj) {
      var r = confirm("Thao tác xoá sẽ không thể phục hồi lại. Bạn có muốn xoá dữ liệu?");
      if (r == true) {
          
      } else {
          return;
      }

      this.isLoading = true;

      var service = new ProductPostingService();
      await service.deleteProductPostingById(obj.id);

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    menuClicked (obj) {
      console.log('menuClicked ', obj.menu_code, obj.id);
      var service = new ProductPostingService();

      if(obj.menu_code == 'status_finished') {
        service.updateProductStatus(obj.id, 'Đã hoàn tất');
      } else if(obj.menu_code == 'status_posting') {
        service.updateProductStatus(obj.id, 'Đang đăng tin');
      } else if(obj.menu_code == 'view_detail') {
        let routeData = this.$router.resolve({path: `/v2/products/${obj.id}/posting`});
        window.open(routeData.href, '_blank');
      }

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    async updateGroupClicked (obj) {
      var r = confirm("Thao tác cập nhật không thể phục hồi lại. Bạn có muốn?");
      if (r == true) {
          
      } else {
          return;
      }

      this.isLoading = true;

      var service = new ProductPostingService();
      if(obj.menu_code == 'status_finished') {
        service.updateGroupProductsStatus(obj.ids, 'Đã hoàn tất');
      } else if(obj.menu_code == 'status_posting') {
        service.updateGroupProductsStatus(obj.ids, 'Đang đăng tin');
      }

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    async deleteGroupClicked (obj) {
      var r = confirm("Thao tác xoá sẽ không thể phục hồi lại. Bạn có muốn xoá dữ liệu?");
      if (r == true) {
          
      } else {
          return;
      }

      this.isLoading = true;

      var service = new ProductPostingService();
      await service.deleteGroupProductPostings(obj.ids);

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    async updateStar (obj) {
      console.log('updateStar ', obj.id, obj.isfavored);

      this.isLoading = true;

      var service = new ProductPostingService();
      await service.updateProductColumn(obj.id, 'isfavored', obj.isfavored, 'boolean');

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    rowPreprocessFunc (item) {
      if(item.posting_reminder > 0 && item.posting_status==='Đang đăng tin') {
        if(item.posting_reminder < item.days_since_last_posting) {
          item._rowVariant='danger';
        }
      }
      return item;
    },

    clearAllFilters() {
      // Reset to first page when applying filters
      this.mobilePage = 1;
      this.loadMobileData();
      
      // Also refresh desktop table if it exists
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(true);
      }
      
      // Close the drawer
      this.closeFilterDrawer();
    },

    applyFilters() {
      // Reset to first page when applying filters
      this.mobilePage = 1;
      this.loadMobileData();
      
      // Also refresh desktop table if it exists
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(true);
      }
      
      // Close the drawer
      this.closeFilterDrawer();
    },

    initializeFilters() {
      // Ensure all filters have proper initial values
      console.log('ProductPostingList: Initializing filters:', this.filters);
      this.filters.forEach(filter => {
        if (filter.type === 'multi-select') {
          if (!Array.isArray(filter.value)) {
            console.log('ProductPostingList: Converting multi-select filter to array:', filter.col, filter.value);
            filter.value = filter.value ? [filter.value] : [];
          }
        }
        if (filter.type === 'number') {
          if (!filter.from) filter.from = null;
          if (!filter.to) filter.to = null;
        }
        if (filter.type === 'boolean' && filter.value === undefined) {
          filter.value = false;
        }
      });
    },

    openFilterDrawer() {
      console.log('Opening filter drawer...');
      this.showFilterDrawer = true;
    },

    closeFilterDrawer() {
      console.log('Closing filter drawer...');
      this.showFilterDrawer = false;
    }
  },
  props: [],
  watch: {
    mobilePage() {
      this.loadMobileData();
    }
  }
}
</script>

<style scoped>
.product-posting-list {
  padding: 0;
  width: 100%;
  overflow-x: hidden;
}

/* Mobile Styles */
.mobile-view {
  padding: 0 10px;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
}

.mobile-filters {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 10px 0;
}

.mobile-summary {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.mobile-cards {
  margin-bottom: 20px;
}

.mobile-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transform: translateY(-1px);
}

.mobile-card.danger {
  border-left: 4px solid #dc3545;
}

.mobile-card .card-header {
  padding: 12px 15px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.mobile-card .card-title {
  flex: 1;
  min-width: 0;
}

.mobile-card .card-title strong {
  color: #495057;
  font-size: 1rem;
}

.mobile-card .card-title small {
  font-size: 0.875rem;
  color: #6c757d;
  word-break: break-word;
}

.mobile-card .card-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.mobile-card .card-body {
  padding: 15px;
}

.mobile-card .info-row {
  display: flex;
  margin-bottom: 8px;
  align-items: flex-start;
}

.mobile-card .info-row:last-child {
  margin-bottom: 0;
}

.mobile-card .label {
  font-weight: 600;
  color: #495057;
  min-width: 120px;
  flex-shrink: 0;
  font-size: 0.875rem;
}

.mobile-card .value {
  color: #6c757d;
  font-size: 0.875rem;
  word-break: break-word;
  flex: 1;
}

.mobile-card .card-footer {
  padding: 12px 15px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

.mobile-card .card-footer .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.mobile-pagination {
  padding: 20px 0;
}

.mobile-pagination .pagination {
  margin-bottom: 0;
}

.mobile-pagination .page-link {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

/* Force mobile/desktop view based on screen size */
@media (max-width: 768px) {
  .desktop-view {
    display: none !important;
  }
  
  .mobile-view {
    display: block !important;
  }
  
  /* Ensure mobile view takes full width */
  .product-posting-list .mobile-view {
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 5px !important;
  }
}

@media (min-width: 769px) {
  .mobile-view {
    display: none !important;
  }
  
  .desktop-view {
    display: block !important;
  }
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .mobile-view {
    padding: 0 5px;
  }
  
  .mobile-card .card-header,
  .mobile-card .card-body,
  .mobile-card .card-footer {
    padding: 10px;
  }
  
  .mobile-card .label {
    min-width: 100px;
    font-size: 0.8rem;
  }
  
  .mobile-card .value {
    font-size: 0.8rem;
  }
  
  .mobile-card .card-footer .btn {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
}

/* Utility classes */
.clickable {
  cursor: pointer;
}

.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-primary {
  color: #007bff !important;
}

.text-muted {
  color: #6c757d !important;
}

/* Loading state */
.mobile-card.loading {
  opacity: 0.6;
  pointer-events: none;
}

/* Loading and empty states */
.mobile-loading,
.mobile-empty {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-loading .spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Animation for card appearance */
.mobile-card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Badge styling for active filters */
.mobile-filters .badge,
.desktop-filters .badge {
  font-size: 0.7rem;
  padding: 0.2em 0.4em;
}

.desktop-filters {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

/* Hide desktop filter button on mobile */
@media (max-width: 768px) {
  .desktop-filters {
    display: none !important;
  }
}
</style>
